import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { MediaserverAudioSettingResponse } from 'features/mediaserver-settings/domain/models/mediaserver-audio-setting-response';
import { UpdateMediaserverAudioSettingCommand } from 'features/mediaserver-settings/domain/models/update-mediaserver-audio-setting-command';

const baseUrl = '/api/v1/Settings';

const readMediaserverAudioSetting = async (axiosInstance: AxiosInstance): Promise<MediaserverAudioSettingResponse> => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/ReadMediaserverAudioSetting`);

    return response.data as MediaserverAudioSettingResponse;
  } catch (error) {
    throw new Error('Error while calling ReadMediaserverAudioSetting', {
      cause: error,
    });
  }
};

const updateMediaserverAudioSetting = async (axiosInstance: AxiosInstance, command: UpdateMediaserverAudioSettingCommand) => {
  const requestBody = JSON.stringify(command);
  const response = await axiosInstance.put(`${baseUrl}/UpdateMediaserverAudioSetting`, requestBody);
  return response.data;
};

export const useReadMediaserverAudioSettingQuery = (): UseQueryResult<MediaserverAudioSettingResponse> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: ['ReadMediaserverAudioSetting'],
    queryFn: () => readMediaserverAudioSetting(authenticatedInstance),
  });
};

export const useUpdateMediaserverAudioSettingMutation = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['UpdateMediaserverAudioSetting'],
    mutationFn: (updateMediaserverAudioSettingCommand: UpdateMediaserverAudioSettingCommand) =>
      updateMediaserverAudioSetting(authenticatedInstance, updateMediaserverAudioSettingCommand),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ReadMediaserverAudioSetting'],
      });
    },
  });
};
